/**
 * Shared PDF design system for all Planner exports.
 *
 * Designed for monochrome (black & white) printing by default:
 *   - No coloured status fills (they reproduce as muddy greys).
 *   - Status differentiation via font weight (bold = confirmed, italic = cancelled).
 *   - High-contrast black/grey palette + thin hairlines for hierarchy.
 *
 * The Proslava.ba logo is rasterised once per page lifetime and rendered in the
 * header by `drawPDFHeader`. Callers should `await loadPDFLogo()` at the top of
 * each export so the cache is warm before any header is drawn.
 */

import type { jsPDF } from "jspdf"

/**
 * Lazy-load `jspdf` + `jspdf-autotable` together.
 *
 * The PDF libraries are ~200 kB gzipped — far too heavy to ship in the initial
 * bundle of `/spisak-gostiju`, `/zadaci`, `/kalukator-budzeta`, `/profil`.
 * They are only needed the moment the user clicks an "Export to PDF" button,
 * so we import them on demand and return the constructor + plugin.
 *
 * Returns `{ jsPDF, autoTable }`. The plugin attaches itself to jsPDF as a
 * side effect of being imported, but callers may also invoke it directly.
 */
export async function loadPDF(): Promise<{
  jsPDF: typeof import("jspdf").jsPDF
  autoTable: typeof import("jspdf-autotable").default
}> {
  const [{ jsPDF }, autoTableModule] = await Promise.all([
    import("jspdf"),
    import("jspdf-autotable"),
  ])
  return { jsPDF, autoTable: autoTableModule.default }
}

// ─── Colour palette ──────────────────────────────────────────────────────────
/**
 * RGB tuples for jsPDF set*Color helpers.
 *
 * The palette is intentionally monochrome-first so exports look identical on
 * colour and B&W printers. Named colour tokens (`green`/`orange`/`red`) are
 * kept for backward compatibility but resolve to neutral greys.
 */
export const C = {
  primary:      [15,  23,  42]  as [number, number, number],   // near-black (slate-900)
  primaryMid:   [51,  65,  85]  as [number, number, number],   // slate-700
  primaryLight: [241, 245, 249] as [number, number, number],   // slate-100
  dark:         [15,  23,  42]  as [number, number, number],   // slate-900
  gray:         [100, 116, 139] as [number, number, number],   // slate-500
  grayLight:    [248, 250, 252] as [number, number, number],   // slate-50
  border:       [203, 213, 225] as [number, number, number],   // slate-300
  white:        [255, 255, 255] as [number, number, number],
  // Status accents — kept for legacy callers; deliberately desaturated so they
  // print well in B&W as different shades of grey.
  green:        [30,  41,  59]  as [number, number, number],   // slate-800 (bold dark)
  greenLight:   [241, 245, 249] as [number, number, number],
  orange:       [71,  85,  105] as [number, number, number],   // slate-600
  orangeLight:  [248, 250, 252] as [number, number, number],
  red:          [100, 116, 139] as [number, number, number],   // slate-500
  redLight:     [241, 245, 249] as [number, number, number],
} as const

// ─── Serbian text normalisation ──────────────────────────────────────────────
/**
 * Replace Serbian special characters with ASCII equivalents so built-in
 * Helvetica renders them correctly (č→c, ć→c, š→s, ž→z, đ→dj …).
 */
export function fixSerbianText(text: string): string {
  if (!text) return text
  return text
    .replace(/č/g, "c").replace(/ć/g, "c")
    .replace(/š/g, "s").replace(/ž/g, "z").replace(/đ/g, "dj")
    .replace(/Č/g, "C").replace(/Ć/g, "C")
    .replace(/Š/g, "S").replace(/Ž/g, "Z").replace(/Đ/g, "Dj")
}

/** Legacy compat – no-op; kept so existing callers compile. */
export function configurePDFForSerbian(doc: jsPDF): jsPDF { return doc }
/** Returns the PDF font name used across all exports. */
export function getPDFFont(): string { return "helvetica" }

// ─── Page layout constants ────────────────────────────────────────────────────
export const MARGIN = 15          // left & right margin (mm)
export const HEADER_H = 32        // height of the top header band (mm)
export const FOOTER_Y_FROM_BOTTOM = 14  // footer line measured from page bottom
export const CONTENT_W = 180      // usable content width (A4 210 − 2×15)

// ─── Logo loader (SVG → cached PNG data URL) ─────────────────────────────────
/** Source aspect ratio of `/logo-dark.svg` — width / height. */
const LOGO_SVG_RATIO = 1737 / 606
const LOGO_PRINT_WIDTH_MM = 30

let cachedLogoPng: string | null = null
let pendingLogoLoad: Promise<string | null> | null = null

/**
 * Fetch the brand logo, rasterise it via `<canvas>` and return a PNG data URL.
 *
 * The result is cached on the module scope so repeated PDF exports do not
 * re-fetch or re-render the logo. Returns `null` when running outside the
 * browser or when the asset cannot be loaded — `drawPDFHeader` falls back to a
 * clean text-only header in that case.
 */
export async function loadPDFLogo(): Promise<string | null> {
  if (cachedLogoPng) return cachedLogoPng
  if (pendingLogoLoad) return pendingLogoLoad
  if (typeof window === "undefined") return null

  pendingLogoLoad = (async () => {
    try {
      const res = await fetch("/logo-dark.svg")
      if (!res.ok) return null
      const svgText = await res.text()
      const dataUrl =
        "data:image/svg+xml;base64," +
        btoa(unescape(encodeURIComponent(svgText)))

      const img = new Image()
      img.src = dataUrl
      await new Promise<void>((resolve, reject) => {
        img.onload = () => resolve()
        img.onerror = () => reject(new Error("Logo load failed"))
      })

      const scale = 3
      const w = (img.naturalWidth || 1737) * scale
      const h = (img.naturalHeight || 606) * scale
      const canvas = document.createElement("canvas")
      canvas.width = w
      canvas.height = h
      const ctx = canvas.getContext("2d")
      if (!ctx) return null
      ctx.fillStyle = "#ffffff"
      ctx.fillRect(0, 0, w, h)
      ctx.drawImage(img, 0, 0, w, h)
      cachedLogoPng = canvas.toDataURL("image/png")
      return cachedLogoPng
    } catch {
      return null
    } finally {
      pendingLogoLoad = null
    }
  })()

  return pendingLogoLoad
}

// ─── Header ──────────────────────────────────────────────────────────────────
/**
 * Draw the branded page header. Returns the Y where body content can start.
 *
 * Layout (monochrome-first):
 *   ┌──────────────────────────────────────────────────────────┐
 *   │ [LOGO]                                          TITLE    │
 *   │                                                 subtitle │
 *   └──────────────────────────────────────────────────────────┘  ← hairline
 */
export function drawPDFHeader(
  doc: jsPDF,
  title: string,
  subtitle?: string,
): number {
  const pw = doc.internal.pageSize.getWidth()

  // Logo on the left (rendered only if `loadPDFLogo()` was awaited beforehand).
  if (cachedLogoPng) {
    const w = LOGO_PRINT_WIDTH_MM
    const h = w / LOGO_SVG_RATIO
    const y = (HEADER_H - h) / 2
    try {
      doc.addImage(cachedLogoPng, "PNG", MARGIN, y, w, h)
    } catch {
      /* Defensive: addImage will throw on a corrupted data URL — render text
       * fallback instead of breaking the export. */
      doc.setFont("helvetica", "bold")
      doc.setFontSize(13)
      doc.setTextColor(...C.dark)
      doc.text("proslava.ba", MARGIN, HEADER_H / 2 + 2)
    }
  } else {
    doc.setFont("helvetica", "bold")
    doc.setFontSize(13)
    doc.setTextColor(...C.dark)
    doc.text("proslava.ba", MARGIN, HEADER_H / 2 + 2)
  }

  // Title (right-aligned, bold black).
  doc.setFont("helvetica", "bold")
  doc.setFontSize(15)
  doc.setTextColor(...C.dark)
  const titleY = subtitle ? HEADER_H / 2 - 1 : HEADER_H / 2 + 2
  doc.text(fixSerbianText(title), pw - MARGIN, titleY, { align: "right" })

  // Subtitle (right-aligned, small grey).
  if (subtitle) {
    doc.setFont("helvetica", "normal")
    doc.setFontSize(8.5)
    doc.setTextColor(...C.gray)
    doc.text(fixSerbianText(subtitle), pw - MARGIN, HEADER_H / 2 + 5, {
      align: "right",
    })
  }

  // Hairline separator below the band.
  doc.setDrawColor(...C.dark)
  doc.setLineWidth(0.5)
  doc.line(MARGIN, HEADER_H, pw - MARGIN, HEADER_H)

  return HEADER_H + 8 // first content Y
}

// ─── Footer ──────────────────────────────────────────────────────────────────
/**
 * Draw the branded footer on the current page.
 * Call once per page, ideally right before adding the next page.
 */
export function drawPDFFooter(
  doc: jsPDF,
  pageNum: number,
  totalPages?: number,
): void {
  const pw = doc.internal.pageSize.getWidth()
  const ph = doc.internal.pageSize.getHeight()
  const lineY = ph - FOOTER_Y_FROM_BOTTOM

  doc.setDrawColor(...C.border)
  doc.setLineWidth(0.3)
  doc.line(MARGIN, lineY, pw - MARGIN, lineY)

  // Brand (left).
  doc.setFont("helvetica", "bold")
  doc.setFontSize(7.5)
  doc.setTextColor(...C.dark)
  doc.text("proslava.ba", MARGIN, lineY + 5)

  // Page number (right).
  doc.setFont("helvetica", "normal")
  doc.setFontSize(7.5)
  doc.setTextColor(...C.gray)
  const pageLabel = totalPages
    ? `Strana ${pageNum} / ${totalPages}`
    : `Strana ${pageNum}`
  doc.text(pageLabel, pw - MARGIN, lineY + 5, { align: "right" })
}

// ─── Section label ────────────────────────────────────────────────────────────
/**
 * Draw a left-accented section heading (e.g. "Sto 1").
 * Returns the Y below the element.
 */
export function drawSectionLabel(doc: jsPDF, label: string, y: number): number {
  doc.setFillColor(...C.dark)
  doc.rect(MARGIN, y, 2.5, 6, "F")

  doc.setFont("helvetica", "bold")
  doc.setFontSize(11)
  doc.setTextColor(...C.dark)
  doc.text(fixSerbianText(label), MARGIN + 5.5, y + 5)

  return y + 12
}

// ─── Summary stat chips ───────────────────────────────────────────────────────
type StatChip = { label: string; value: string; color?: [number, number, number] }

/**
 * Draw a horizontal row of stat chips (label + value pairs in bordered boxes).
 * Returns the Y below the row.
 *
 * Monochrome design: every chip uses the same neutral style — white card with
 * a thin black border, bold black value, small grey caption. The `color` field
 * on `StatChip` is accepted for backward compatibility but intentionally
 * ignored so all chips reproduce identically in B&W print.
 */
export function drawStatRow(doc: jsPDF, stats: StatChip[], y: number): number {
  if (stats.length === 0) return y

  const gap = 4
  const boxW = (CONTENT_W - (stats.length - 1) * gap) / stats.length
  const boxH = 18
  let x = MARGIN

  for (const stat of stats) {
    // Card body
    doc.setFillColor(...C.white)
    doc.setDrawColor(...C.border)
    doc.setLineWidth(0.4)
    doc.roundedRect(x, y, boxW, boxH, 2.5, 2.5, "FD")

    // Value (bold black)
    doc.setFont("helvetica", "bold")
    doc.setFontSize(14)
    doc.setTextColor(...C.dark)
    doc.text(stat.value, x + boxW / 2, y + 8.5, { align: "center" })

    // Label (small grey, uppercase tracking-feel via spacing)
    doc.setFont("helvetica", "normal")
    doc.setFontSize(7.5)
    doc.setTextColor(...C.gray)
    doc.text(fixSerbianText(stat.label.toUpperCase()), x + boxW / 2, y + 14.5, {
      align: "center",
    })

    x += boxW + gap
  }

  return y + boxH + 8
}

// ─── Table checkbox (manual tick on print) ───────────────────────────────────
/** Draw an empty rounded square centred in an autotable body cell. */
export function drawPDFTableCheckbox(
  doc: jsPDF,
  cell: { x: number; y: number; width: number; height: number },
  size = 4,
): void {
  const cx = cell.x + (cell.width - size) / 2
  const cy = cell.y + (cell.height - size) / 2
  doc.setDrawColor(...C.dark)
  doc.setLineWidth(0.4)
  doc.setFillColor(...C.white)
  doc.roundedRect(cx, cy, size, size, 0.7, 0.7, "FD")
}

// ─── autoTable shared base options ───────────────────────────────────────────
/**
 * Returns the base autotable options that every table export should spread
 * and then extend with `columnStyles` and `startY`.
 *
 * Monochrome-friendly: black header row, white body, very subtle alternating
 * rows that still render distinct under a B&W toner.
 */
/** Shared compact row layout: 10pt body, vertically centred, tighter padding. */
const PDF_TABLE_COMPACT = {
  fontSize: 10,
  valign: "middle" as const,
  halign: "left" as const,
  cellPadding: { top: 2, bottom: 2, left: 6, right: 6 },
  minCellHeight: 7,
}

/** Slightly taller rows — used for Zadaci and Pregled troškova tables. */
const PDF_TABLE_RELAXED = {
  cellPadding: { top: 3.5, bottom: 3.5, left: 6, right: 6 },
  minCellHeight: 9,
}

/** Merge relaxed row height into base table styles (spread after `getPDFTableStyles()`). */
export function withPDFRelaxedRows(
  base: Record<string, unknown>,
): { styles: object; bodyStyles: object } {
  return {
    styles: { ...(base.styles as object), ...PDF_TABLE_RELAXED },
    bodyStyles: { ...(base.bodyStyles as object), ...PDF_TABLE_RELAXED },
  }
}

export function getPDFTableStyles(): Record<string, unknown> {
  return {
    theme: "plain",
    tableWidth: CONTENT_W,
    headStyles: {
      fillColor: C.dark,
      textColor: C.white,
      fontStyle: "bold",
      fontSize: 9.5,
      font: "helvetica",
      halign: "left",
      valign: "middle",
      cellPadding: { top: 3, bottom: 3, left: 6, right: 6 },
      lineWidth: 0,
    },
    bodyStyles: {
      ...PDF_TABLE_COMPACT,
      font: "helvetica",
      textColor: C.dark,
      lineColor: C.border,
      lineWidth: 0.2,
    },
    alternateRowStyles: {
      fillColor: C.grayLight,
    },
    styles: {
      ...PDF_TABLE_COMPACT,
      font: "helvetica",
      lineColor: C.border,
      lineWidth: 0.2,
      overflow: "linebreak",
    },
    margin: { left: MARGIN, right: MARGIN },
  }
}

// ─── Safe usable page height ──────────────────────────────────────────────────
/** Maximum Y a content element may reach before a manual page break is needed. */
export function maxContentY(doc: jsPDF): number {
  return doc.internal.pageSize.getHeight() - FOOTER_Y_FROM_BOTTOM - 6
}
